import { Injectable } from '@angular/core';
import { CanonData } from '@models/Canons';
import { CurrentPosition } from '@models/CurrentPosition';
import { ProjectConfiguration, ProjectId } from '@models/ProjectConfiguration';
import { Verse } from '@models/Verse';
import { VerseReference } from '@models/VerseReference';
import { Canon } from '@models/Canon';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { UserId, UserProfile } from '@models/UserProfile';
import { CognitoService } from './cognito.service';
import { CognitoUserInfoResponse } from '@models/TimedOauthCredentials';

@Injectable({
  providedIn: 'root'
})
export class SynchronizerService {
  private _user$: BehaviorSubject<UserProfile | undefined> = new BehaviorSubject<UserProfile | undefined>(undefined);
  private _position$: BehaviorSubject<CurrentPosition | undefined> = new BehaviorSubject<CurrentPosition | undefined>(undefined);
  private _verse$: BehaviorSubject<Verse | undefined | null> = new BehaviorSubject<Verse | undefined | null>(undefined);
  public reference$: BehaviorSubject<VerseReference | undefined> = new BehaviorSubject<VerseReference | undefined>(undefined);

  constructor(private api: ApiService, public cognitoService: CognitoService) {
    this._verse$.subscribe((verse) => {
      if (verse) {
        this.reference$.next(verse.reference);
      }
    });
  }

  async initializeApplication(): Promise<void> {
    try {
      const userInfo: CognitoUserInfoResponse = await this.cognitoService.initializeApplication();
      /// We've now got the user authenticated from the cognito service

      try {
        const user = await this.api.getUserData(userInfo.username);
        if (user === undefined) {
          this.user$.next(undefined);
          console.error("User data is undefined. We should insert the user into the database.");
        } else {
          /// add it to the local data
          this.user$.next(user);
          /// counterintuitively, we need to get the user
          /// data from the server again, so that it will
          /// include all of the appropriate project data
          await this.setCurrentUser(userInfo.username);
          /// get the current position
          this._readCurrentPositionFromLocalStorage();
        }
      } catch (error) {
        console.error("Error getting user data", error);
        this.user$.next(undefined);
      }
    } catch (error) {
      console.error("Error with cognito initializeApplication. Apparently a failure of cognitoService.initializeApplication.", error);
      this._user$.next(undefined);
    }

  }

  get verse$(): BehaviorSubject<Verse | undefined | null> {
    return this._verse$;
  }

  get user$(): BehaviorSubject<UserProfile | undefined> {
    return this._user$;
  }

  /// Observables are read-only, so this prevents the value from being changed from outside the service
  get position$(): Observable<CurrentPosition | undefined> {
    return this._position$.asObservable();
  }

  /* Convenience functions */
  get currentProject(): ProjectConfiguration | undefined {
    return this._user$.value?.projectFromPosition(this._position$.value);
  }

  get currentPosition(): CurrentPosition | undefined {
    return this._position$.value;
  }

  get currentCanonData(): CanonData | undefined {
    return this.currentPosition?.canonData;
  }

  get currentUser(): UserProfile | undefined {
    return this._user$.value;
  }

  setCanon(c: Canon) {
    if (this.currentPosition) {
      /// confirmed that comparing objects of type Canon checks the string level
      if (this.currentPosition.canon !== c) {
        this.currentPosition.canon = c;
        // this._position$.next(this.currentPosition);
        this._handleVerseTransition(this.currentPosition.ref);
      }
    }
  }

  setProject(id: ProjectId, c: Canon) {
    if (this.currentPosition) {
      /// confirmed that comparing objects of type ProjectId and Canon checks the string level
      if (this.currentPosition.project_id !== id || this.currentPosition.canon !== c) {
        this.currentPosition.changeProject(id, c);
        // this._position$.next(this.currentPosition);
        this._handleVerseTransition(this.currentPosition.ref);
      }
    }
  }

  updateVerse(newRef: VerseReference) {
    /// if there is no current verse, then we need to get it from the API
    if (this.currentPosition === undefined) {
      this._handleVerseTransition(newRef);
    }
    /// if the current verse is different from the new verse, then we need to update the current verse
    else if (!this.currentPosition.ref.equals(newRef)) {
      this._handleVerseTransition(newRef);
    }
  }

  async saveVerseData() {
    const hasChangedGlosses: boolean = this.verse?.hasChangedGlosses() || false;
    const project_id: ProjectId | undefined = this.currentProject?.id;
    const canon: Canon | undefined = this.currentCanonData?.name;

    ///  handle the update logic
    if (this.verse && hasChangedGlosses && project_id && canon) {
      /// if an update is required...
      await this.api.updateVerse(canon, this.verse, project_id)
        .catch(err => {
          console.error(`Error updating verse: ${err}`);
          console.error(canon, this.verse, project_id);
        });
    }
  }

  reloadUserData(): Promise<UserProfile> {
    if (this.currentUser?.user_id) {
      return this.setCurrentUser(this.currentUser.user_id);
    } else {
      return Promise.reject("No user_id to reload user data");
    }
  }

  setCurrentUser(user_id: UserId): Promise<UserProfile> {
    return this.api.getUserData(user_id)
      .then((user) => {
        this.user$.next(user);
        return user;
      });
  }


  /* Private methods */

  /// convenience
  private get verse(): Verse | undefined | null {
    return this._verse$.value;
  }

  _writeCurrentPositionToLocalStorage() {
    if (this._position$.value) {
      localStorage.setItem("currentPosition", JSON.stringify(this._position$.value.toObject()));
    }
  }

  _readCurrentPositionFromLocalStorage() {
    const currentPosition: string | null = localStorage.getItem("currentPosition");
    if (currentPosition) {
      const position = CurrentPosition.fromJson(currentPosition);
      if (position) {
        this._position$.next(position);
        this._handleVerseTransition(position?.ref);
      }
    } else if (this._user$.value?.projects) {
      this.setToFallbackPosition();
      if (this._position$.value) {
        this._handleVerseTransition(this._position$.value.ref);
      }
    }
  }

  setToFallbackPosition() {
    if (this._user$.value?.projects) {
      const projects = Array.from(this._user$.value?.projects);
      const fallback = CurrentPosition.createFromProjectList(projects);
      if (fallback) {
        this._position$.next(fallback);
      }
    } else {
      console.error("User has no projects, so cannot set to fallback position");
    }
  }

  deepCopyVerseToTriggerRefresh() {
    if (this.verse) {
      const copy = this.verse.copyOf();
      this._verse$.next(copy);
    }
  }

  /// This should only be called when it is already known that ref
  /// is different from the current verse
  private async _handleVerseTransition(ref: VerseReference) {
    const hasChangedGlosses: boolean = this.verse?.hasChangedGlosses() || false;
    const user_id: UserId | undefined = this.currentUser?.user_id;
    const project_id: ProjectId | undefined = this.currentProject?.id;
    const canon: Canon | undefined = this.currentCanonData?.name;

    ///  handle the update logic
    if (this.verse && hasChangedGlosses && project_id && canon) {
      /// if an update is required...

      /// 2024-08-30: These were formerly chained, but I don't see
      /// the need for them to be run sequentially. Should help lag
      /// times as well.
      /// 2025-05-02: We need to await for the result here. Otherwise
      /// you enter a word on one verse, and then when you move to the
      /// next verse, if that word appears, the gloss still doesn't show up.
      /// The lag is unavoidable unless I embed a verse request in the
      /// updateVerse function. Maybe some day.
      await this.api.updateVerse(canon, this.verse, project_id)
        .catch(err => {
          console.error(`Error updating verse: ${err}`);
          console.error(canon, this.verse, project_id);
        });
    }
    /// load the new verse
    if (project_id && user_id) {
      this._getVerseFromApi(ref, user_id, project_id);
    } else {
      console.error("Ignoring request in _handleVerseTransition: projectId or user_id is undefined", project_id, user_id);
      // console.trace();
    }
  }

  forceVerseReloadWithoutSendingData() {
    const ref = this._position$.value?.ref;
    const user_id = this.currentUser?.user_id;
    const project_id = this.currentProject?.id;
    if (!ref || !user_id || !project_id) {
      console.error("Ignoring request in forceVerseReloadWithoutSendingData: ref, projectId or user_id is undefined", ref, project_id, user_id);
      return;
    }
    this._getVerseFromApi(ref, user_id, project_id);
  }

  private _getVerseFromApi(ref: VerseReference, userId: UserId, projectId: ProjectId) {
    this._verse$.next(undefined);

    this.api.loadVerseFromAPI(ref, userId, projectId).then(verse => {
      this._verse$.next(verse);
      if (verse) {
        this._position$.value?.setCurrentReference(projectId, verse.reference);
        this._position$.next(this._position$.value);
        this._writeCurrentPositionToLocalStorage();
      }
    })
      .catch(err => {
        console.error(`Error loading verse from API: ${err}`);
      });
  }

}